<script>
import Pagination from './pagination.vue';
import states from '../lib/states';

export default {
  name: 'Directory',
  components: {
    pagination: Pagination,
  },
  data: function() {
    return {
      isLoading: true,
      sessionId: '',
      // Thumbnails whose file 404s at request time. Keyed by member id so one bad image
      // falls back to the placeholder without disturbing the rest of the table.
      failedThumbs: {},
      // Distinguishes "nobody matched" from "Neon did not answer" -- both arrive as an
      // empty member list, but they are not the same thing to tell a visitor.
      loadError: false,
      criteria: {
        name: '',
        city: '',
        state: '',
        profileType: '',
        relationshipToTheField: '',
        media: '',
      },
      states,
      searchTerm: '',
      sortBy: '',
      sortOrder: '',
      members: [],
      page: 1,
      totalPages: 0,
      totalResults: 0,
    };
  },
  created() {
    this.sessionId = Math.random()
      .toString(36)
      .substr(2, 9);
    this.loadResults();
  },
  computed: {
    hasFilters() {
      return Object.keys(this.criteria).some(key => this.criteria[key] !== '');
    },
  },
  methods: {
    initials(member) {
      const parts = (member.fullName || '').trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return '?';
      if (member.profileType === 'organization' || parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
      }
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
    avatarStyle(member) {
      // Hashed from the Neon id on the server, so a member's placeholder is the same colour
      // everywhere it appears. The stylesheet grey remains the fallback.
      const colors = member.avatarColors;
      return colors ? { backgroundColor: colors.background, color: colors.foreground } : {};
    },
    thumbFailed(member) {
      this.$set(this.failedThumbs, member.id, true);
    },
    navigate(member) {
      if (member.slug) {
        window.location = `/directory/${member.slug}`;
      }
    },
    loadResults(page = false) {
      this.isLoading = true;
      this.members = [];

      this.$http
        .post('/actions/neon-members/members', {
          criteria: this.criteria,
          sessionId: this.sessionId,
          page: this.page,
          sort: this.sortBy ? `${this.sortBy} ${this.sortOrder}` : null,
          [window.site.csrfTokenName]: window.site.csrfTokenValue,
        })
        .then(
          function(body) {
            const data = body || {};
            const page = data.page || {};
            this.members = data.members || [];
            this.totalPages = page.totalPage || 0;
            this.totalResults = page.totalResults || 0;
            this.loadError = Boolean(data.error);
            this.isLoading = false;
          }.bind(this)
        )
        .catch(
          function() {
            // Neon is occasionally unavailable; show an empty result rather than
            // leaving the spinner up with an unhandled rejection in the console.
            this.members = [];
            this.totalPages = 0;
            this.totalResults = 0;
            this.loadError = true;
            this.isLoading = false;
          }.bind(this)
        );
    },
    applyFilters() {
      this.page = 1;
      this.loadResults();
    },
    clearFilters() {
      Object.keys(this.criteria).forEach(key => {
        this.criteria[key] = '';
      });
      this.applyFilters();
    },
    gotoPage(page = 1) {
      if (page !== false) {
        this.page = page;
      }
      this.loadResults();
    },
    setSort(col) {
      if (col === this.sortBy) {
        this.sortOrder = this.sortOrder == 'ASC' ? 'DESC' : 'ASC';
      } else {
        this.sortBy = col;
        this.sortOrder = 'ASC';
        this.page = 1;
      }

      this.loadResults();
    },
  },
};
</script>

<template>
  <div class="c-directory">
    <div class="u-flex u-jc-between u-ai-center u-mb-1 c-directory__filters">
      <div class="o-flex__item c-directory__filter-by">
        <label for="directory-type" class="c-directory__filter-label">Filter by</label>
        <select
          id="directory-type"
          v-model="criteria.profileType"
          @change="applyFilters"
          class="c-form__input c-directory__filter-select"
        >
          <option value="">All members</option>
          <option value="individual">Individuals</option>
          <option value="organization">Organizations</option>
          <option value="student">Students</option>
        </select>
      </div>
      <div class="o-flex__item">
        <span v-show="totalPages > 0">Showing {{page}} of {{totalPages}} pages</span>
      </div>
      <form v-show="false" @submit.prevent="performSearch" class="o-flex__item u-1/2">
        <div class="c-form__input-group">
          <input
            v-model="searchTerm"
            type="search"
            placeholder="Find a member…"
            aria-label="Find a member"
            class="c-form__input c-form__input--sm c-form__input--rounded u-2/3"
          >
          <button type="submit" class="c-btn c-btn--sm u-1/3">
            <span>Search</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 50 50"
              width="16"
              height="16"
              fill="#ffffff"
            >
              <title>Search</title>
              <path
                d="M50 42.188l-14.672-14.672c1.386-2.616 2.172-5.599 2.172-8.765 0-10.355-8.395-18.75-18.75-18.75s-18.75 8.395-18.75 18.75 8.395 18.75 18.75 18.75c3.167 0 6.149-0.786 8.765-2.172l14.672 14.672 7.813-7.813zM6.25 18.75c0-6.904 5.596-12.5 12.5-12.5s12.5 5.596 12.5 12.5-5.596 12.5-12.5 12.5-12.5-5.596-12.5-12.5z"
              ></path>
            </svg>
          </button>
        </div>
      </form>
    </div>
    <table class="c-table" style="table-layout:fixed; width:100%;">
      <thead class="u-type-small@lt-sm">
        <tr>
          <th class="c-directory__thumb-col" scope="col">
            <span class="u-visually-hidden">Photo</span>
          </th>
          <th scope="col">
            <button
              @click="setSort('name')"
              :class="{
                'c-table__sortable--selected': sortBy == 'name',
                'c-table__sortable--reverse': sortBy == 'name' && sortOrder == 'DESC'
              }"
              class="c-table__sortable"
            >Name</button>
          </th>
          <th class="u-hidden@lt-md" scope="col">
            <button
              @click="setSort('city')"
              :class="{
                'c-table__sortable--selected': sortBy == 'city',
                'c-table__sortable--reverse': sortBy == 'city' && sortOrder == 'DESC'
              }"
              class="c-table__sortable"
            >City</button>
          </th>
          <th scope="col">
            <button
              @click="setSort('state')"
              :class="{
                'c-table__sortable--selected': sortBy == 'state',
                'c-table__sortable--reverse': sortBy == 'state' && sortOrder == 'DESC'
              }"
              class="c-table__sortable"
            >State</button>
          </th>
          <th class="u-hidden@lt-lg" scope="col">Status</th>
          <th class="u-hidden@lt-lg" scope="col">Medium</th>
          <th class="link-col" scope="col">
            <span class="u-hidden@lt-sm">Profile</span>
          </th>
        </tr>
        <tr class="c-table__filter-row">
          <td></td>
          <td class="c-table__filter-cell">
            <input
              v-model="criteria.name"
              @change="applyFilters"
              class="directory-search"
              type="text"
              autocomplete="off"
              placeholder="Search…"
              aria-label="Search by name"
            >
          </td>
          <td class="c-table__filter-cell u-hidden@lt-md">
            <input
              v-model="criteria.city"
              @change="applyFilters"
              class="directory-search"
              type="text"
              autocomplete="off"
              placeholder="Search…"
            >
          </td>
          <td class="c-table__filter-cell">
            <div>
              <select v-model="criteria.state" @change="applyFilters" class="select">
                <option value>Search…</option>
                <option
                  v-for="state in states"
                  v-bind:key="state.abbreviation"
                  :value="state.abbreviation"
                >{{state.abbreviation}}</option>
              </select>
            </div>
          </td>
          <td class="u-hidden@lt-lg">
            <input
              v-model="criteria.relationshipToTheField"
              @change="applyFilters"
              class="directory-search"
              type="text"
              autocomplete="off"
              placeholder="Search…"
            >
          </td>
          <td class="u-hidden@lt-lg">
            <input
              v-model="criteria.media"
              @change="applyFilters"
              class="directory-search"
              type="text"
              autocomplete="off"
              placeholder="Search…"
            >
          </td>
          <td></td>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!isLoading && !members.length" class="c-directory__empty-row">
          <td colspan="7" class="c-directory__empty">
            <template v-if="loadError">
              <p>The member directory is temporarily unavailable.</p>
              <p class="c-directory__empty-hint">Please try again in a few minutes.</p>
            </template>
            <template v-else-if="hasFilters">
              <p>No members match those filters.</p>
              <button type="button" @click="clearFilters" class="c-btn c-btn--sm">
                Clear filters
              </button>
            </template>
            <p v-else>No members to show.</p>
          </td>
        </tr>
        <tr
          v-for="member in members"
          v-bind:key="member.id"
          v-on:click="navigate(member)"
          :id="member.id"
          class="directory-row"
        >
          <td class="c-directory__thumb-col">
            <img
              v-if="member.thumbnailUrl && !failedThumbs[member.id]"
              :src="member.thumbnailUrl"
              @error="thumbFailed(member)"
              class="c-directory__thumb"
              width="48"
              height="48"
              loading="lazy"
              alt=""
            >
            <span
              v-else
              :style="avatarStyle(member)"
              class="c-directory__thumb c-directory__thumb--empty"
              aria-hidden="true"
            >
              {{initials(member)}}
            </span>
          </td>
          <td data-title="Name">
            {{member.fullName}}
            <span v-if="member.profileType === 'organization'" class="c-directory__org-tag">
              Organization
            </span>
            <span v-else-if="member.parentOrganization" class="c-directory__org-tag">
              <a
                v-if="member.parentOrganization.slug"
                :href="`/directory/${member.parentOrganization.slug}`"
                @click.stop
              >{{member.parentOrganization.name}}</a>
              <template v-else>{{member.parentOrganization.name}}</template>
            </span>
          </td>
          <td data-title="City" class="u-hidden@lt-md">{{member.city}}</td>
          <td data-title="State">{{member.state}}</td>
          <td
            data-title="Professional Status"
            class="u-hidden@lt-lg"
          >{{member.relationshipToTheField}}</td>
          <td data-title="Medium" class="u-hidden@lt-lg">{{member.media}}</td>
          <td>
            <a :href="`/directory/${member.slug}`" v-show="member.slug" class="directory-profile">
              <span class="u-hidden@lt-sm">View Profile</span> →
            </a>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-show="isLoading" class="c-spinner"></div>
    <pagination
      :total-pages="totalPages"
      :current-page="page"
      @pagechanged="gotoPage"
    />
  </div>
</template>

<style lang="scss" scoped>
// Members sometimes paste a URL into Medium, and an unbroken string that long spills
// over the Profile link beside it -- .c-table is table-layout: fixed, so the column
// cannot widen to take it. Applied to every body cell rather than just Medium, since
// Name, City and Status can all receive the same free text.
.c-directory td {
  overflow-wrap: break-word;
  // Body line-height is 1.5, which is generous for a dense table -- organization names
  // wrap onto two or three lines and the rows drifted apart.
  line-height: 1.3;
}

// Palette values are literal rather than color() -- Vite does not inject the settings
// partial into single-file component styles.
// 48px of image plus the 18px of left padding .c-table puts on every cell. The right
// padding is dropped so the avatar sits near the name rather than marooned.
.c-directory__thumb-col {
  width: 4.5rem;
  padding-right: 0;
}

.c-directory__thumb {
  display: block;
  width: 48px;
  height: 48px;
  // The base reset applies `max-width: 100%` to every img. With height pinned at 48px,
  // any cell narrower than that squashed the photo into a vertical ellipse -- min-width
  // keeps it round even if something upstream tightens the column again.
  max-width: none;
  min-width: 48px;
  border-radius: 50%;
  object-fit: cover;
  background-color: #f3f7f9;
}

// Placeholder for the members with no photo on file, which is a good number of them.
// Sized identically to the image so a mixed table does not jump row to row.
.c-directory__thumb--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: bold;
  line-height: 1;
  // Fallback only: each placeholder gets its member's own colours inline.
  color: #70818a;
  background-color: #dae4e9;
}

.c-directory__empty {
  padding-top: 3rem;
  padding-bottom: 3rem;
  text-align: center;

  p {
    margin-bottom: 1rem;
  }

  p:last-child {
    margin-bottom: 0;
  }
}

.c-directory__empty-hint {
  color: #70818a;
}

// .c-table highlights rows on hover because they are clickable. This one is neither a
// member nor clickable, so it should stay put.
.c-directory .c-directory__empty-row:hover > td {
  background-color: transparent;
}

.c-directory__filter-by {
  display: flex;
  align-items: center;
}

.c-directory__filter-label {
  margin-right: 0.75rem;
  font-weight: bold;
  white-space: nowrap;
}

// Full-size rather than the --sm used for the filter inputs inside the table head: this
// is the one control on the page people are expected to reach for first.
.c-directory__filter-select {
  width: auto;
  min-width: 15rem;
}

// Only 23 of 462 members are organizations, so a whole column reading "Individual" on
// every other row was poor value for the width. The distinction lives here and in the
// toolbar filter instead.
// Darker than the palette's gray-dark (#70818a), which at this size managed 4.04:1 on
// white and 3.58:1 on the hover row. This clears AA at 5.69:1 and 5.04:1.
.c-directory__org-tag {
  display: block;
  font-size: 0.75rem;
  line-height: 1.2;
  color: #5a6970;

  // Brand orange is 3.34:1 on white, short of AA for text this small, so the parent
  // organization link takes the tag's colour and relies on its underline instead.
  a {
    color: inherit;
    text-decoration: underline;
  }
}

.directory-profile {
  font-weight: bold;
  text-decoration: none;
  white-space: nowrap;

  &:hover,
  &:focus {
    text-decoration: underline;
  }
}

@media (max-width: 499px) {
  th {
    white-space: nowrap;
  }

  th,
  td {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  th:last-child,
  td:last-child {
    padding-left: 0;
    padding-right: 0;
    text-align: center;
  }

  .c-table__sortable::after {
    display: none;
  }

  .link-col {
    width: 10%;
  }
}
</style>
